import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Input, Popup, SemanticICONS, Tab } from 'semantic-ui-react';
import { useDebounce } from 'use-lodash-debounce';
import { SURVEY_STATUS_KEYS } from '~/Constants';
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery';
import Layout from '~/components/Layout';
import TableComponent from '~/components/TableComponent';
import i18n from '~/components/i18n';
import { RouteTypes } from '~/routes/constants';
import PreviewModal from './PreviewModal';
import { Link } from 'react-router-dom';
import CustomIcon from '~/components/CustomIcon';
import { DownloadService } from '~/api/services/downloadFiles';
import { handleErrors } from '~/utils/helpers';
import InfoPopup from '~/components/InfoPopup';

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Period under consideration'),
    key: 'period',
    type: 'climate-check-period'
  },
  {
    name: i18n.t('Title/Topic'),
    key: 'title'
  },
  {
    name: i18n.t('Start/end data collection'),
    key: 'start',
    type: 'startEndSurvey',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
];

const Co2Climate: React.FC = () => {
  const { t } = useTranslation()
  const actions = [
    {
      popupMessage: i18n.t('Continue data collection'),
      name: 'icon-survey-2',
      type: 'link',
      link: RouteTypes.CO2_CLIMATE_DRAFT_ANSWER,
      subFarmerLink: RouteTypes.CO2_CLIMATE_DRAFT_SUB_FARMER_ANSWER,
      renderActions: getActions,
      actionType: 'continue-co2'
    },
    {
      popupMessage: i18n.t('Start data collection'),
      name: 'icon-answer-survey',
      link: RouteTypes.CO2_CLIMATE_ANSWER,
      subFarmerLink: RouteTypes.CO2_CLIMATE_SUB_FARMER_ANSWER,
      type: 'link',
      renderActions: getActions,
      actionType: 'answer-co2'
    },
    {
      popupMessage: i18n.t('Show'),
      name: 'eye',
      type: 'icon',
      renderActions: getActions,
      actionType: 'show-co2'
    },
    {
      popupMessage: i18n.t('Edit data collection'),
      name: 'pencil',
      link: RouteTypes.CO2_CLIMATE_EDIT_ANSWER,
      subFarmerLink: RouteTypes.CO2_CLIMATE_EDIT_SUB_FARMER_ANSWER,
      type: 'link',
      renderActions: getActions,
      actionType: 'edit-co2'
    },
    {
      popupMessage: <span dangerouslySetInnerHTML={{ __html: i18n.t('CO2 emission result') }} />,
      name: 'file pdf',
      type: 'icon',
      actionType: 'results',
      size: 'large',
      verifiedActionKey: 'completed',
      verifiedAction: 1,
      renderActions: getActions
    }
  ]

  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [search, setSearch] = useState('')
  const [uniqueId, setUniqueId] = useState(null)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [previewModalId, setPreviewModalId] = useState(null)
  const debouncedSearchValue = useDebounce(search, 300)
  const [tableColumnHeaders, setTableColumnHeaders] = useState([])
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    search: '',
    status: 'actual',
    column: '',
    sort: '',
    farmerId: ''
  })

  const {
    data: climateData,
    isLoading: isBusyData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = CO2ClimateQuery.getCO2Footprints({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  const {
    data: climateOptions,
    isLoading: isBusyOptions,
    refetch: fetchOptions
  } = CO2ClimateQuery.getCO2FootprintsOptions({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  const {
    data: climateCount,
    isLoading: isBusyCount,
    refetch: fetchCount
  } = CO2ClimateQuery.getCO2Count({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
    search: debouncedSearchValue
  })

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    fetchFilteredData()
    fetchOptions()
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
    }
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  useEffect(() => {
    fetchCount()
  }, [dataFiltered.farmerId])

  function getActionLink(action, id) {
    const useSubFarmersLink = climateOptions?.subFarmers?.length > 0 && dataFiltered.farmerId !== ''
    const link = useSubFarmersLink ? action.subFarmerLink.replace(':id', id).replace(':farmerId', dataFiltered.farmerId) : action.link.replace(':id', id)
    return <Link to={link}>
      <CustomIcon style={{ cursor: 'pointer' }} name={action.name} size='large' />
    </Link>
  }

  function getActions(co2, actionType) {
    const { completed, hasAnswer, status, id } = co2
    const isLive = status === SURVEY_STATUS_KEYS.live
    const displayContinueAction = isLive && hasAnswer === 1 && completed === 0
    const displayEditAction = isLive && hasAnswer === 1 && completed === 1
    const displayAnswerAction = isLive && hasAnswer === 0 && completed === 0
    const displayShowAction = completed === 1
    const action = actions.find(action => action.actionType === actionType)
    const displayResults = climateOptions?.showResultsInLiveStatus === 1 ? displayShowAction : displayShowAction && status === SURVEY_STATUS_KEYS.completed
    switch (actionType) {
      case 'continue-co2':
        return displayContinueAction && <Popup
          content={action.popupMessage}
          trigger={getActionLink(action, id)}
        />
      case 'answer-co2':
        return displayAnswerAction && <Popup
          content={action.popupMessage}
          trigger={getActionLink(action, id)}
        />
      case 'show-co2':
        return displayShowAction && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }}
            onClick={() => handleShow(co2)}
            name={action.name as SemanticICONS}
            size='large'
          />}
        />
      case 'edit-co2':
        return displayEditAction && <Popup
          content={action.popupMessage}
          trigger={getActionLink(action, id)}
        />
      case 'results':
        return displayResults && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }}
            onClick={() => handleDownloadResults(co2)}
            name={action.name as SemanticICONS}
            size='large'
          />}
        />
      default:
        return <></>
    }
  }

  function renderSearch() {
    return (
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = climateOptions?.statuses.find((_, indexOption) => indexOption === activeIndex);
      setDataFiltered({
        ...dataFiltered,
        status: status.key,
        page: 0
      });
      setActiveTabIndex(activeIndex)
    }
  }

  function handleShow(row) {
    setPreviewModalId(row?.id)
    setUniqueId(row?.uniqueId)
  }

  async function handleDownloadResults(row) {
    setLoadingAction({
      type: 'results',
      id: row.id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFileWithName(`co2-footprints/${row?.id}/get-results/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleClickAction(row, type) {
    switch (type) {
      case 'results':
        return handleDownloadResults(row)
      default:
        return
    }
  }

  function renderContent() {
    return <TableComponent
      actions={actions}
      isFetchingData={isFetchingData}
      loadingAction={loadingAction}
      animated
      tableColumnHeaders={tableColumnHeaders}
      handleClickAction={handleClickAction}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function renderPanes() {
    const panes = climateOptions?.statuses?.map((option) => {
      const countIndex = climateCount.findIndex((count) => count.status == option.key);
      return {
        menuItem: `${option.label}  (${climateCount[countIndex]?.count || 0})`,
        render: () => renderContent()
      };
    });

    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeTabIndex}
        panes={panes}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  function renderPreviewModal() {
    return <PreviewModal
      id={previewModalId}
      handleClose={() => {
        setPreviewModalId(null)
        setUniqueId(null)
      }}
      uniqueId={uniqueId}
      farmerId={dataFiltered.farmerId}
    />
  }

  function renderActions() {
    return <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Dropdown
        selectOnBlur={false}
        placeholder={t('Select farmer')}
        options={climateOptions?.subFarmers?.map(subFarmer => ({ text: subFarmer.companyName, value: subFarmer?.id }))}
        value={dataFiltered.farmerId || climateOptions?.subFarmers[0]?.id}
        onChange={(e, data) => {
          setDataFiltered({
            ...dataFiltered,
            farmerId: data.value as string
          })
        }
        }
        selection
      />
      <InfoPopup
        content={t('You have several operating locations, each of which requires independent data collection. \n\
    You can use the drop-down menu to access your other business locations and start or edit the data collection.')}
        styleIcon={{ fontSize: '1.4em' }}
        position={'left center'}
      />
    </div>
  }

  const { filtered, rows } = climateData ? climateData : { filtered: 0, rows: [] };
  const isLoading = isBusyData || isBusyCount || isBusyOptions

  return <Layout
    isBusy={isLoading}
    title={<span dangerouslySetInnerHTML={{ __html: t('CO2 Klima Check') }} />}
    isCo2Loader={true}
    filters={renderSearch()}
    actions={climateOptions?.subFarmers?.length > 0 && renderActions()}
    segmented
  >
    {climateData && climateCount && climateOptions && renderPanes()}
    {renderPreviewModal()}
  </Layout>
};

export default Co2Climate;